/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */
import { React, useState, useEffect } from 'react';
import {withStyles} from '@material-ui/core/styles';
import ResponseJsonView from './views/ResponseJsonView.jsx';
import SurveyResponseView from './views/SurveyResponseView.jsx';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import CreateResponseJsonView from './views/CreateResponseJsonView.jsx';
import CreateResponseFormView from './views/ResponseFormView.jsx';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
    surveyResponseDetails,
} from '../assets/table-details.js';

import Button from "@material-ui/core/Button";
import TableFilter from "./table/TableFilter.jsx";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import Table from "./table/Table.jsx";
import DynamoDBIcon from 'react-aws-icons/dist/aws/logo/DynamoDB';
import SQSIcon from 'react-aws-icons/dist/aws/logo/SQS';
import DynamoDBService from "../js/service/DynamoDBService";
import {useAlert} from "react-alert";
import '../css/DataContainer.css'
import SurveyService from "../js/service/SurveyService";
import HttpService from "../js/service/HttpService";
import Select from '@material-ui/core/Select';
import FormGroup from "@material-ui/core/FormGroup";



let currentTab = 0;

export default function DataContainer(props) {

    /**
     * Local Constants
     */
    const httpService = new HttpService(props.oktaAuth);
    const dbService = new DynamoDBService(httpService);
    const surveyService = new SurveyService(httpService);
    const alert = useAlert();

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [createViewOpen, setCreateViewOpen] = useState(false);
    const [details, setDetails] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [createViewSelection, setCreateViewSelection] = useState("form");
    const [searchId, setSearchId] = useState("");
    const [surveyId, setSurveyId] = useState("sleep-concierge-v1")
    const [publishTo, setPublishTo] = useState("dynamodb");
    const [currentSurveyDefinition, setCurrentSurveyDefinition] = useState({questions: {}});
    const [viewTab, setViewTab] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState('en-us');
    const [langJson, setLangJson] = useState("")
  

    /**
     * Scans DynamoDB and updates the table data.
     *
     * This is done every time the page renders, as well as whenever a survey response is added/deleted and when filter/search bar is used
     */
    async function loadNextPage(refresh) {
       
        const sourceId = `${searchId}`;


        let filter;

        // Gets current tab or default
        switch (currentTab) {
            case 0:
                filter = "SURVEY_RESPONSE";
                break;

            case 1:
                filter = "SURVEY_SCHEDULE";
                break;

            default:
                filter = "SURVEY_RESPONSE";
                break;
        }

        let sortKey = filter + "#" + surveyId;

        let data;

        // If something is in search bar, do a quick query to see if it matches a single item (quicker then scanning)
        if (searchId) {
            data = await dbService.queryBySourceId(sourceId, sortKey);

            if(data['Item'] !== undefined){

               setDetails(data['Item']);
            }
            else{
                setDetails(data);
            }        

            setDetailsOpen(true);

        }
    }

    /**
     * Refreshes table
     */
    async function updateTable() {
        await loadNextPage(true);
    }

    /**
     * Deletes Item given response id and sort key
     *
     * @param responseId response id of item
     * @param sKey       sort key of item
     */
    const deleteItem = (responseId, sKey) => {
        dbService.deleteKeys(responseId, sKey, alert, updateTable);
    }

    /**
     * Handles click for create button
     *
     * @param event details of click
     */
    const createButtonHandleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Handles close of Menu Selection
     */
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    /**
     * Handles tab changes by updating currentTab value and updating table
     *
     * @param event details on tab change
     * @param newValue new value of tab
     */
    const handleTabChange = (event, newValue) => {
        currentTab = newValue;
        updateTable();
    };
    /**
     * Handles tab changes by updating newValue and updating the setViewTab
     *
     * @param event details on tab change
     * @param newValue new value of tab
     */
    const handleViewChange = (event, newValue) => {
        setViewTab(newValue);
    };

    /**
     * Handler for opening response details
     *
     * @param json response in json form
     */
    const handleResponseDetailsOpen = (json) => {
        setDetails(json);
        setDetailsOpen(true);
    }

    /**
     * Checks if current tab is response tab
     */
    const isResponseTable = () => {
        return currentTab === 0;
    }


    /**
     * On open, get default survey definition
     *
     */
    useEffect(() => {
        surveyService.getSurveyDefinitionFromAPI(surveyId)
            .then(result => setCurrentSurveyDefinition(result))
            .catch(error => httpService.log(error));
        downloadAndSetLanguageFile("en-us")
    }, [])

    /**
     * On surveyId change, get the current survey definition.
     *
     */
    useEffect(() => {
        surveyService.getSurveyDefinitionFromAPI(surveyId)
            .then(result => setCurrentSurveyDefinition(result))
            .catch(error => httpService.log(error));

    }, [surveyId])
    

    function handleLanguageChange(lang) {
        downloadAndSetLanguageFile(lang.target.value)
    }

    function downloadAndSetLanguageFile(lang) {
        new HttpService().fetch(`${process.env.REACT_APP_SCUI_BASE_URL}/assets/i18n/${lang}.json`).then(response => {
            response.json().then(json => {
                setLangJson(json)
            })
        })
        setSelectedLanguage(lang)
    }

    const TableToolbar = () => {
        return (
            <Toolbar
                className='dataContainer-toolbarRoot'
            >
                <FormGroup row >
                    <Select value={selectedLanguage} onChange={handleLanguageChange}>
                        <MenuItem value={'en-us'}>English (United States)</MenuItem>
                        <MenuItem value={'ar'}>Arabic</MenuItem>
                        <MenuItem value={'bg'}>Bulgarian</MenuItem>
                        <MenuItem value={'cs'}>Czech</MenuItem>
                        <MenuItem value={'da'}>Danish</MenuItem>
                        <MenuItem value={'de'}>German</MenuItem>
                        <MenuItem value={'el'}>Greek</MenuItem>
                        <MenuItem value={'en'}>English - ROW</MenuItem>
                        <MenuItem value={'es'}>Spanish (Latin America)</MenuItem>
                        <MenuItem value={'es-es'}>Spanish</MenuItem>
                        <MenuItem value={'et'}>Estonian</MenuItem>
                        <MenuItem value={'fi'}>Finnish</MenuItem>
                        <MenuItem value={'fr-ca'}>French (Canadian)</MenuItem>
                        <MenuItem value={'fr'}>French</MenuItem>
                        <MenuItem value={'hr'}>Croatian</MenuItem>
                        <MenuItem value={'hu'}>Hungarian</MenuItem>
                        <MenuItem value={'id'}>Bahasa Indonesia</MenuItem>
                        <MenuItem value={'is'}>Icelandic</MenuItem>
                        <MenuItem value={'it'}>Italian</MenuItem>
                        <MenuItem value={'ja'}>Japanese</MenuItem>
                        <MenuItem value={'ko'}>Korean</MenuItem>
                        <MenuItem value={'lt'}>Lithuanian</MenuItem>
                        <MenuItem value={'lv'}>Latvian</MenuItem>
                        <MenuItem value={'ms'}>Bahasa Melayu</MenuItem>
                        <MenuItem value={'nb'}>Norwegian</MenuItem>
                        <MenuItem value={'nl'}>Dutch</MenuItem>
                        <MenuItem value={'pl'}>Polish</MenuItem>
                        <MenuItem value={'pt-br'}>Portuguese (Brazil)</MenuItem>
                        <MenuItem value={'pt'}>Portuguese (Portugal)</MenuItem>
                        <MenuItem value={'ro'}>Romanian</MenuItem>
                        <MenuItem value={'ru'}>Russian</MenuItem>
                        <MenuItem value={'sl'}>Slovenian</MenuItem>
                        <MenuItem value={'sv'}>Swedish</MenuItem>
                        <MenuItem value={'th'}>Thai</MenuItem>
                        <MenuItem value={'tr'}>Turkish</MenuItem>
                        <MenuItem value={'zh-hans'}>Chinese Simplified</MenuItem>
                        <MenuItem value={'zh'}>Chinese Traditional</MenuItem>
                    </Select>
                </FormGroup>
                <div className='dataContainer-toolbarTitleButtonWrapper'>
                    <Button
                        variant="outlined"
                        color="primary"
                        className='dataContainer-createButton'
                        onClick={createButtonHandleClick}
                        startIcon={<NoteAddIcon />}
                        data-testid="createButton"
                    >
                    </Button>
                    <CreateViewSelection
                        id="create-view-selection-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        data-testid="selectionMenu"
                    >
                        {isResponseTable() ? <MenuSelection type={"form"} label={"Generate Object (SQS)"} icon={<SQSIcon />} sqs /> : null}
                        <MenuSelection type={"json"} label={"Edit JSON Manually"} icon={<DynamoDBIcon />} />
                    </CreateViewSelection>
                </div>
                <TableFilter
                    updateTable={updateTable}
                    setSearchId={setSearchId}
                    searchId   ={searchId}
                    surveyId   ={surveyId}
                    setSurveyId={setSurveyId}
                />
            </Toolbar>
        );
    };

    const MenuSelection = (props) => {
        return (
            <MenuItem onClick={() => {
                setPublishTo(props.sqs ? "SQS" : "DynamoDB");
                setCreateViewSelection(props.type);
                setCreateViewOpen(!createViewOpen);
                handleMenuClose();
            }}>
                {props.icon}
                <Typography  variant="body1" id="tableTitle" component="div" style={{ marginLeft: "10px" }}>
                    {props.label}
                </Typography>
            </MenuItem>
        );
    }

    return (
        <>
            <div className='dataContainer-root'>
            <Table
                tableToolBar={TableToolbar}
            /> 
            {details &&
                <>
                <div className='dataContainer-toolbarTableControl'>       
                    <Tabs
                        value={viewTab}
                        onChange={handleViewChange}

                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab label="RESPONSE VIEW" />
                        <Tab label="JSON VIEW" />
                        
                    </Tabs>
                </div> 
                    {viewTab === 1 &&  
                        <ResponseJsonView
                            open={detailsOpen}
                            setOpen={setDetailsOpen}
                            details={details}
                        />
                    }           
                    {viewTab === 0 &&
                    <SurveyResponseView
                            open={detailsOpen}
                            setOpen={setDetailsOpen}
                            details={details}
                            langJson={langJson}
                        />
                    }
                </>
            }    
                {createViewSelection === "json" ? (
                        <CreateResponseJsonView
                            open={createViewOpen}
                            setOpen={setCreateViewOpen}
                            keys={surveyResponseDetails}
                            updateTable={updateTable}
                            isResponse={isResponseTable}
                        />
                    ) :
                    (
                        <CreateResponseFormView
                            open={createViewOpen}
                            setOpen={setCreateViewOpen}
                            updateTable={updateTable}
                            publishTo={publishTo}
                            surveyDefinition={currentSurveyDefinition}
                            oktaAuth={props.oktaAuth}
                            langJson={langJson}
                        />
                    )}
                <hr />
            </div>
        </>
    )
}

const CreateViewSelection = withStyles({
    paper: {
        border: '1px solid #d3d4d5',
    },
})((props) => (
    <Menu
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
        }}
        {...props}
    />
));